/**
 * URL helpers for the agent mode panel (pendo-split-pane trailing slot).
 * Panel visibility is driven by the `leo` query param so open/close
 * participates in browser history.
 */

const PARAM_KEY = 'leo';
const AGENT_ANALYTICS_REPORT_ROUTE_NAME = 'AiAgentReport';
const LEO_PAGE_ROUTE_NAMES = ['LeoConversation', 'LeoHomepage'];

const agentModePanelChangeListeners = new Set();

/**
 * Subscribe to Leo panel URL changes made via history.replaceState (no Vue Router navigation).
 * @param {() => void} listener
 * @returns {() => void} unsubscribe
 */
export function subscribeAgentModePanelChange (listener) {
    agentModePanelChangeListeners.add(listener);

    return () => agentModePanelChangeListeners.delete(listener);
}

export function notifyAgentModePanelChange () {
    agentModePanelChangeListeners.forEach((listener) => listener());
}

/**
 * @param {import('vue-router').RouteLocationNormalizedLoaded | undefined} route
 * @returns {boolean}
 */
export function isAgentAnalyticsReportRoute (route) {
    return route?.name === AGENT_ANALYTICS_REPORT_ROUTE_NAME;
}

/**
 * Whether the route is one of Leo's own full-page views, which own the screen rather than
 * sitting behind the drawer.
 * @param {import('vue-router').RouteLocationNormalizedLoaded | undefined} route
 * @returns {boolean}
 */
export function isLeoPageRoute (route) {
    return LEO_PAGE_ROUTE_NAMES.includes(route?.name);
}

/**
 * The panel param to carry onto the next route, so a drawer opened from the top nav stays open
 * while the user navigates — following Leo's step-by-step instructions means leaving the page
 * the conversation started on.
 *
 * Returns null when there is nothing to carry, or when the param was dropped deliberately:
 * closing the drawer navigates to the same path without it, and starting a new conversation
 * hands the screen over to Leo's own page.
 *
 * @param {import('vue-router').RouteLocationNormalized} to
 * @param {import('vue-router').RouteLocationNormalized} from
 * @returns {{ leo: string } | null}
 */
export function getAgentModePanelQueryForNavigation (to, from) {
    if (to.path === from.path || to.query?.[PARAM_KEY] || isLeoPageRoute(to)) {
        return null;
    }

    const value = getAgentModePanelValue(from);

    return value ? { [PARAM_KEY]: value } : null;
}

function getAgentModePanelValueFromLocation () {
    if (typeof window === 'undefined') {
        return null;
    }

    const value = new URLSearchParams(window.location.search).get(PARAM_KEY);

    return value && String(value).trim() ? String(value).trim() : null;
}

function isExpandedInLocation () {
    if (typeof window === 'undefined') {
        return false;
    }

    return new URLSearchParams(window.location.search).has('expanded');
}

function mutateLocationSearch (mutator) {
    if (typeof window === 'undefined') {
        return;
    }

    const url = new URL(window.location.href);
    mutator(url.searchParams);
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
    notifyAgentModePanelChange();
}

function openAgentModePanelViaHistory (value) {
    mutateLocationSearch((params) => {
        params.set(PARAM_KEY, value);
    });
}

function closeAgentModePanelViaHistory () {
    mutateLocationSearch((params) => {
        params.delete(PARAM_KEY);
        params.delete('expanded');
    });
}

function toggleAgentModePanelExpandedViaHistory () {
    mutateLocationSearch((params) => {
        if (params.has('expanded')) {
            params.delete('expanded');
        } else {
            params.set('expanded', 'true');
        }
    });
}

/**
 * Returns whether the agent mode panel is considered open from the route's query.
 * @param {import('vue-router').RouteLocationNormalizedLoaded | undefined} route
 * @returns {boolean}
 */
export function isAgentModePanelOpen (route) {
    return Boolean(getAgentModePanelValue(route));
}

/**
 * Returns the value of the panel query param, or null if absent.
 * @param {import('vue-router').RouteLocationNormalizedLoaded | undefined} route
 * @returns {string | null}
 */
export function getAgentModePanelValue (route) {
    if (isAgentAnalyticsReportRoute(route)) {
        return getAgentModePanelValueFromLocation();
    }

    const routeValue = route?.query?.[PARAM_KEY];
    if (routeValue != null && String(routeValue).trim()) {
        return String(routeValue).trim();
    }

    return getAgentModePanelValueFromLocation();
}

/**
 * Opens the agent mode panel by adding/updating the panel query param.
 * On Agent Analytics, uses history.replaceState (not Vue Router) to avoid re-initializing the report.
 * @param {import('vue-router').Router} router
 * @param {import('vue-router').RouteLocationNormalizedLoaded} route
 * @param {string} [value='open']
 * @param {{ replace?: boolean }} [options]
 */
export function openAgentModePanel (router, route, value = 'open', options = {}) {
    if (isAgentAnalyticsReportRoute(route)) {
        openAgentModePanelViaHistory(value);

        return;
    }

    const { replace = false } = options;
    const method = replace ? 'replace' : 'push';

    router[method]({
        path: route.path,
        query: {
            ...route.query,
            [PARAM_KEY]: value,
        },
    });
}

/**
 * Returns a copy of the query object with the panel param and `expanded` removed.
 * @param {Record<string, string>} [query={}]
 * @returns {Record<string, string>}
 */
export function stripAgentModePanelParams (query = {}) {
    const { [PARAM_KEY]: _panelParam, expanded: _expanded, ...rest } = query;

    return rest;
}

/**
 * Closes the agent mode panel by removing the panel query param.
 * @param {import('vue-router').Router} router
 * @param {import('vue-router').RouteLocationNormalizedLoaded} route
 * @param {{ replace?: boolean }} [options]
 */
export function closeAgentModePanel (router, route, options = {}) {
    if (isAgentAnalyticsReportRoute(route)) {
        closeAgentModePanelViaHistory();

        return;
    }

    const { replace = false } = options;
    const method = replace ? 'replace' : 'push';

    router[method]({
        path: route.path,
        query: stripAgentModePanelParams(route.query),
    });
}

/**
 * Returns whether the agent mode panel is expanded from the route's query.
 * @param {import('vue-router').RouteLocationNormalizedLoaded | undefined} route
 * @returns {boolean}
 */
export function isAgentModePanelExpanded (route) {
    if (isAgentAnalyticsReportRoute(route)) {
        return isExpandedInLocation();
    }

    if ('expanded' in (route?.query || {})) {
        return true;
    }

    return isExpandedInLocation();
}

/**
 * Toggles the `expanded` query param for the agent mode panel.
 * Adds it when absent, removes it when present.
 * @param {import('vue-router').Router} router
 * @param {import('vue-router').RouteLocationNormalizedLoaded} route
 * @param {{ replace?: boolean }} [options]
 */
export function toggleAgentModePanelExpanded (router, route, options = {}) {
    if (isAgentAnalyticsReportRoute(route)) {
        toggleAgentModePanelExpandedViaHistory();

        return;
    }

    const { replace = false } = options;
    const method = replace ? 'replace' : 'push';
    const query = route.query || {};
    const isExpanded = 'expanded' in query;

    if (isExpanded) {
        const { expanded: _expanded, ...rest } = query;

        router[method]({
            path: route.path,
            query: rest,
        });
    } else {
        router[method]({
            path: route.path,
            query: {
                ...query,
                expanded: 'true',
            },
        });
    }
}
